import React, { useState } from 'react';
import { Edit2, Save, X, Lock, User, Phone, Mail, Eye, EyeOff } from 'lucide-react';
import { supabase } from '../../../lib/supabase';
import Icon from '../../../components/AppIcon';


const DriverAccountSettings = ({ profile, onUpdate }) => {
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({
    fullName: profile?.name || '',
    phone: profile?.phone || '',
    email: profile?.email || '',
  });
  const [passwordData, setPasswordData] = useState({ newPass: '', confirm: '' });
  const [showPasswords, setShowPasswords] = useState({ newPass: false, confirm: false });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleSaveField = async (field) => {
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');
      if (field === 'fullName' || field === 'phone') {
        await supabase?.from('user_profiles')?.update({
          full_name: field === 'fullName' ? formData?.fullName : undefined,
          phone: field === 'phone' ? formData?.phone : undefined,
        })?.eq('id', user?.id);
      }
      await onUpdate?.({ [field]: formData?.[field] });
      setSuccess('Updated successfully!');
      setEditing(null);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err?.message || 'Update failed.');
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordChange = async () => {
    if (passwordData?.newPass !== passwordData?.confirm) {
      setError('Passwords do not match.');
      return;
    }
    if (passwordData?.newPass?.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const { error: authError } = await supabase?.auth?.updateUser({ password: passwordData?.newPass });
      if (authError) throw authError;
      setSuccess('Password changed successfully!');
      setPasswordData({ newPass: '', confirm: '' });
      setEditing(null);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err?.message || 'Password change failed.');
    } finally {
      setSaving(false);
    }
  };

  const fields = [
    { key: 'fullName', label: 'Full Name', icon: User, type: 'text' },
    { key: 'phone', label: 'Phone Number', icon: Phone, type: 'tel' },
    { key: 'email', label: 'Email Address', icon: Mail, type: 'email', readOnly: true },
  ];

  return (
    <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
      <div className="px-5 py-4 border-b border-slate-100 flex items-center gap-2">
        <User className="w-4 h-4 text-[#1a365d]" />
        <h3 className="font-semibold text-[#1a365d]">Account Settings</h3>
      </div>
      <div className="p-5 space-y-4">
        {(error || success) && (
          <div className={`px-4 py-2.5 rounded-lg text-sm font-medium ${error ? 'bg-red-50 text-red-700 border border-red-200' : 'bg-green-50 text-green-700 border border-green-200'}`}>
            {error || success}
          </div>
        )}

        {fields?.map(({ key, label, icon: Icon, type, readOnly }) => (
          <div key={key} className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-slate-100 flex items-center justify-center flex-shrink-0">
              <Icon className="w-4 h-4 text-slate-500" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs text-slate-500 mb-0.5">{label}</p>
              {editing === key && !readOnly ? (
                <div className="flex items-center gap-2">
                  <input
                    type={type}
                    value={formData?.[key]}
                    onChange={(e) => setFormData(prev => ({ ...prev, [key]: e?.target?.value }))}
                    className="flex-1 text-sm border border-slate-300 rounded-lg px-2.5 py-1.5 focus:outline-none focus:ring-2 focus:ring-[#1a365d]/30 focus:border-[#1a365d]"
                    autoFocus
                  />
                  <button onClick={() => handleSaveField(key)} disabled={saving} className="p-1.5 bg-[#1a365d] text-white rounded-lg hover:bg-[#2d5a87] transition-colors disabled:opacity-50">
                    <Save className="w-3.5 h-3.5" />
                  </button>
                  <button onClick={() => { setEditing(null); setError(''); }} className="p-1.5 bg-slate-100 text-slate-600 rounded-lg hover:bg-slate-200 transition-colors">
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              ) : (
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium text-slate-800 truncate">{formData?.[key] || '—'}</p>
                  {!readOnly && (
                    <button onClick={() => setEditing(key)} className="p-1.5 text-slate-400 hover:text-[#1a365d] hover:bg-slate-100 rounded-lg transition-colors ml-2">
                      <Edit2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        ))}

        <div className="pt-2 border-t border-slate-100">
          {editing === 'password' ? (
            <div className="space-y-3">
              <p className="text-sm font-medium text-slate-700 flex items-center gap-2"><Lock className="w-4 h-4" /> Change Password</p>
              {['newPass', 'confirm']?.map((field) => (
                <div key={field} className="relative">
                  <input
                    type={showPasswords?.[field] ? 'text' : 'password'}
                    placeholder={field === 'newPass' ? 'New password' : 'Confirm new password'}
                    value={passwordData?.[field]}
                    onChange={(e) => setPasswordData(prev => ({ ...prev, [field]: e?.target?.value }))}
                    className="w-full text-sm border border-slate-300 rounded-lg px-3 py-2 pr-10 focus:outline-none focus:ring-2 focus:ring-[#1a365d]/30 focus:border-[#1a365d]"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPasswords(prev => ({ ...prev, [field]: !prev?.[field] }))}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400"
                  >
                    {showPasswords?.[field] ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              ))}
              <div className="flex gap-2">
                <button onClick={handlePasswordChange} disabled={saving} className="btn-dark flex-1 disabled:opacity-50">
                  {saving ? 'Saving...' : 'Update Password'}
                </button>
                <button onClick={() => { setEditing(null); setError(''); }} className="btn-secondary px-4">
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <button onClick={() => setEditing('password')} className="flex items-center gap-2 text-sm text-[#1a365d] font-medium hover:text-[#2d5a87] transition-colors">
              <Lock className="w-4 h-4" />
              Change Password
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default DriverAccountSettings;
